import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Search } from "lucide-react";

const TIER_COLORS = { Local: "#A1A1AA", Regional: "#38BDF8", District: "#818CF8", State: "#FACC15", National: "#F87171", Moneyball: "#FBBF24" };

export default function EventRecordsPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("");
  const [tab, setTab] = useState("tournaments");

  useEffect(() => { api.get("/admin/event-records").then(({ data: d }) => setData(d)).catch(() => {}); }, []);
  if (!data) return null;

  const shown = data.tournaments.filter((r) =>
    (!q || `${r.name} ${r.location} ${r.director || ""}`.toLowerCase().includes(q.toLowerCase())) &&
    (!tier || r.tiers.includes(tier) || (tier === "Moneyball" && r.name.toLowerCase().includes("moneyball"))));

  return (
    <div className="space-y-4" data-testid="event-records-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Completed Events", data.totals.tournaments], ["Moneyballs", data.totals.moneyballs],
          ["Entry Revenue", `$${data.totals.revenue.toFixed(2)}`], ["League Matches", data.totals.league_matches]].map(([l, v]) => (
          <div key={l} className="bg-pb-elevated border border-pb-border p-3">
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</div>
            <div className="font-display font-black text-lg text-lime-300">{v}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[["tournaments", "Tournaments & Moneyballs"], ["league", "League Matches"]].map(([t, l]) => (
          <button key={t} data-testid={`records-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-2 border text-[10px] font-black uppercase tracking-widest transition-colors ${
              tab === t ? "border-pb-red bg-pb-red/10 text-pb-red" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
            {l}
          </button>
        ))}
        {tab === "tournaments" && (
          <>
            <div className="flex items-center gap-2 bg-pb-elevated border border-pb-border px-3 ml-auto">
              <Search className="w-3.5 h-3.5 text-zinc-500" />
              <input data-testid="records-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search records…"
                className="bg-transparent py-2 text-sm text-white focus:outline-none w-36" />
            </div>
            <select data-testid="records-tier-filter" value={tier} onChange={(e) => setTier(e.target.value)}
              className="bg-pb-elevated border border-pb-border px-2 py-2 text-xs text-white focus:outline-none">
              <option value="">All tiers</option>
              {Object.keys(TIER_COLORS).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </>
        )}
      </div>

      {tab === "tournaments" ? (
        <div className="divide-y divide-pb-border/40 border border-pb-border max-h-[480px] overflow-y-auto" data-testid="records-list">
          {shown.length === 0 && <div className="p-4 text-xs text-zinc-600">No completed events yet — finished tournaments land here automatically.</div>}
          {shown.map((r) => (
            <div key={r.id} className="px-3 py-2.5" data-testid={`record-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Trophy className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span className="text-sm font-bold">{r.name}</span>
                {r.tiers.map((t) => (
                  <span key={t} className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border"
                    style={{ color: TIER_COLORS[t], borderColor: `${TIER_COLORS[t]}66` }}>{t}</span>
                ))}
                <span className="ml-auto text-[10px] text-zinc-500 font-mono">{r.date}{r.end_date && r.end_date !== r.date ? ` → ${r.end_date}` : ""}</span>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                {r.location} · {r.entries} entries · ${r.entry_fee} entry · <span className="text-lime-300 font-mono">${r.revenue.toFixed(2)} revenue</span>
                {r.director ? ` · TD: ${r.director}` : ""}
              </div>
              {Object.keys(r.champions).length > 0 && (
                <div className="text-[10px] text-amber-400/90 mt-0.5">
                  🏆 {Object.entries(r.champions).map(([d, n]) => `${d}: ${n}`).join(" · ")}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="divide-y divide-pb-border/40 border border-pb-border max-h-[480px] overflow-y-auto" data-testid="records-league-list">
          {data.league_matches.length === 0 && <div className="p-4 text-xs text-zinc-600">No completed league matches yet.</div>}
          {data.league_matches.map((m) => (
            <div key={m.id} className="px-3 py-2 text-xs flex flex-wrap items-center justify-between gap-2">
              <span className="text-zinc-300">{m.home} vs {m.away} <span className="text-zinc-600">({m.division})</span></span>
              <span className="text-zinc-500">{m.winner ? `W: ${m.winner} · ` : ""}{m.date}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
